/**
 * coachSentence — delegates to the Phase 8 chemistry-coach engine for the
 * seven supported parameters, and falls back to a minimal in/above/below
 * sentence for any other ParameterKey (ammonia, nitrite, iodine, potassium,
 * other) that the coach doesn't cover.
 *
 * The CoachInput interface and coachSentence() signature are unchanged from
 * Phase 6 — only the implementation is upgraded.
 */
import type { ParameterKey } from '@reefapp/shared';
import { getIdealRangeForParameter } from '@reefapp/shared';

import { coachReading } from '../coach/chemistry-coach';
import type { CoachParameter } from '../coach/ranges';

export interface CoachInput {
  parameterKey: ParameterKey;
  value: number;
  unit: string;
  /** Optional historical series; consumed by the Phase 8 engine. */
  series?: ReadonlyArray<{ ts: string; value: number }>;
}

// ---------------------------------------------------------------------------
// ParameterKey → CoachParameter mapping (only the 7 supported parameters)
// ---------------------------------------------------------------------------

const PARAM_KEY_TO_COACH: Partial<Record<ParameterKey, CoachParameter>> = {
  alkalinity: 'alk',
  calcium:    'ca',
  magnesium:  'mg',
  ph:         'pH',
  salinity:   'salinity',
  nitrate:    'nitrate',
  phosphate:  'phosphate',
};

// ---------------------------------------------------------------------------
// Minimal fallback (Phase 6 logic) for unsupported parameters
// ---------------------------------------------------------------------------

function minimalSentence(parameterKey: ParameterKey, value: number, unit: string): string {
  const range = getIdealRangeForParameter(parameterKey);
  const valStr = Number.isFinite(value)
    ? value % 1 === 0
      ? value.toString()
      : value.toFixed(2)
    : '?';
  const unitSuffix = unit ? ` ${unit}` : '';

  if (!range) {
    return `${parameterKey} ${valStr}${unitSuffix}`;
  }

  const { min, max } = range;
  const rangeNote = `ideal ${min}–${max}${unitSuffix}`;

  if (value < min) return `${parameterKey} ${valStr}${unitSuffix} — below range (${rangeNote})`;
  if (value > max) return `${parameterKey} ${valStr}${unitSuffix} — above range (${rangeNote})`;
  return `${parameterKey} ${valStr}${unitSuffix} — in range ✓`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Return one plain-English sentence for the reading.
 * Delegates to the chemistry-coach engine for the 7 supported parameters;
 * falls back to the minimal sentence for all others.
 */
export function coachSentence(input: CoachInput): string {
  const { parameterKey, value, unit, series } = input;
  const coachParam = PARAM_KEY_TO_COACH[parameterKey];

  if (coachParam) {
    try {
      const result = coachReading({ parameter: coachParam, value, unit, series });
      return result.sentence;
    } catch {
      // Range resolution failed (catalog gap) — fall back to minimal.
    }
  }

  return minimalSentence(parameterKey, value, unit);
}
